/**
 * Global event bus. Currently used during initialization.
 *
 * File: \src\MessageBus.ts
 * Created Date: Monday, October 8th 2018, 14:52:44
 * Author: Sascha Herrmann
 * -----
 * Last Modified: November 18th 2024, 13:39:14
 * Modified By: Herrmann Sascha (LDC)
 * -----
 * Copyright (c) 2017-2018 Liebherr-Hausgeräte GmbH
 */
import mitt, { Emitter, EventHandlerMap } from 'mitt';

type Events = {
  STORES_LOADED_EVENT: void;
  USER_AUTHENTICATED_EVENT: void;
  USER_CONFIGURED_EVENT: void;
};

type EventType = keyof Events;

/**
 * The event bus that we use to send global events into the
 * application.
 *
 * @export
 * @class MessageBus
 */
export default class MessageBus {
  /**
   * Create the emitter.
   *
   * @private
   * @static
   * @memberof MessageBus
   */
  private static emitter: Emitter<Events> = mitt<Events>();

  /**
   * Gets the emitters in a map.
   *
   * @static
   * @returns {EventHandlerMap} The current map of emitters.
   * @memberof MessageBus
   */
  public static getAllHandlers(): EventHandlerMap<Events> {
    return this.emitter.all;
  }

  /**
   * Clears out all events for a certain type, named by eventType.
   *
   * @example
   * MessageBus.offAll(MessageBus.PRESET_TEXT_EVENT);
   *
   * @static
   * @param {'*' | EventType} eventType The event type to remove.
   * @returns {boolean} true if the event was found and cleared.
   * @memberof MessageBus
   */
  public static offAll(eventType: '*' | EventType): boolean {
    const events: EventHandlerMap<Events> = this.getAllHandlers();

    if (events) {
      return events.delete(eventType);
    }

    // Event map is not there
    return false;
  }

  /**
   * Call this to emit the event "USER_AUTHENTICATED". This should happen when
   * the user was successfully logged in.
   *
   * @static
   * @memberof MessageBus
   */
  public static emitStoresLoaded(): void {
    MessageBus.emitter.emit('STORES_LOADED_EVENT');
  }

  /**
   * Call this to be notified of the STORES_LOADED event.
   * This event is called when the the stores were loaded.
   *
   * @static
   * @param {() => void} callback The callback to invoke.
   * @memberof MessageBus
   */
  public static onceStoresLoaded(callback: () => void): void {
    const emitterOnceCallback = () => {
      callback();
      MessageBus.emitter.off('STORES_LOADED_EVENT', emitterOnceCallback);
    };

    MessageBus.emitter.on('STORES_LOADED_EVENT', emitterOnceCallback);
  }

  /**
   * Call this to emit the USER_AUTHENTICATED_EVENT event.
   *
   * @static
   * @memberof MessageBus
   */
  public static emitUserAuthenticated(): void {
    MessageBus.emitter.emit('USER_AUTHENTICATED_EVENT');
  }

  /**
   * Call this to be notified of the USER_AUTHENTICATED event.
   * This event is called when the user was successfully authenticated.
   *
   * @static
   * @param {() => void} callback The callback to invoke.
   * @memberof MessageBus
   */
  public static onceUserAuthenticated(callback: () => void): void {
    const emitterOnceCallback = () => {
      callback();
      MessageBus.emitter.off('USER_AUTHENTICATED_EVENT', emitterOnceCallback);
    };

    MessageBus.emitter.on('USER_AUTHENTICATED_EVENT', emitterOnceCallback);
  }

  /**
   * Call this to emit the event "USER_CONFIGURED". This should happen when
   * the user configuration was loaded.
   *
   * @static
   * @memberof MessageBus
   */
  public static emitUserConfigured(): void {
    MessageBus.emitter.emit('USER_CONFIGURED_EVENT');
  }

  /**
   * Call this to be notified of the USER_CONFIGURED event.
   * This event is called when the user configuration was successfully loaded.
   *
   * @static
   * @param {Function} callback The callback to invoke.
   * @memberof MessageBus
   */
  public static onceUserConfigured(callback: () => void): void {
    const emitterOnceCallback = () => {
      callback();
      MessageBus.emitter.off('USER_CONFIGURED_EVENT', emitterOnceCallback);
    };

    MessageBus.emitter.on('USER_CONFIGURED_EVENT', emitterOnceCallback);
  }
}
